import { useState } from 'react';
import { Download, FileText, MapPin, Plus, Trash2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { AddInscricaoModal } from './components/AddInscricaoModal';

export function PasseiosPage() {
  const { data, isLoading, refetch } = trpc.passeios.listarInscricoes.useQuery();
  const cancelar = trpc.passeios.cancelarInscricao.useMutation({
    onSuccess: () => refetch(),
  });
  const [tabAtivo, setTabAtivo] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);

  if (isLoading || !data) {
    return <div className="text-gray-500">Carregando inscrições…</div>;
  }

  const passeioAtivo = data.find((p) => p.id === tabAtivo) ?? data[0];

  const exportarCsv = () => {
    const linhas = [['Passeio', 'Nome', 'Clube', 'Data inscrição']];
    for (const p of data) {
      for (const i of p.inscricoes) {
        linhas.push([
          p.nome,
          i.nomeCompleto,
          i.clube,
          new Date(i.createdAt).toLocaleString('pt-BR'),
        ]);
      }
    }
    const csv = '﻿' + linhas.map((l) => l.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inscricoes-passeios-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarPdf = async () => {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF();
    const dataHoje = new Date().toLocaleDateString('pt-BR');

    doc.setFontSize(16);
    doc.text('Passeios — Conferência LD-8', 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Gerado em ${dataHoje}`, 14, 24);
    doc.setTextColor(0);

    let cursorY = 32;

    for (const p of data) {
      doc.setFontSize(12);
      doc.text(`${p.nome} (${p.inscricoes.length} / ${p.limite})`, 14, cursorY);
      cursorY += 4;

      autoTable(doc, {
        startY: cursorY,
        head: [['#', 'Nome completo', 'Clube', 'Data da inscrição']],
        body:
          p.inscricoes.length > 0
            ? p.inscricoes.map((i, idx) => [
                String(idx + 1),
                i.nomeCompleto,
                i.clube,
                new Date(i.createdAt).toLocaleString('pt-BR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }),
              ])
            : [['', 'Nenhuma inscrição registrada ainda neste passeio.', '', '']],
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [37, 99, 235] },
        margin: { left: 14, right: 14 },
      });

      cursorY =
        (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.save(`inscricoes-passeios-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleCancelar = (id: string, nome: string) => {
    if (window.confirm(`Cancelar a inscrição de ${nome}?`)) {
      cancelar.mutate({ id });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Passeios — Conferência LD-8</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportarCsv} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Exportar CSV
          </Button>
          <Button onClick={exportarPdf} variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            Exportar PDF
          </Button>
          <Button onClick={() => setModalAberto(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nova inscrição
          </Button>
        </div>
      </div>

      <AddInscricaoModal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        passeios={data.map((p) => ({
          id: p.id,
          nome: p.nome,
          limite: p.limite,
          inscritos: p.inscricoes.length,
        }))}
        passeioIdInicial={passeioAtivo?.id}
        onSuccess={() => refetch()}
      />

      {/* Cards-resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.map((p) => {
          const pct = (p.inscricoes.length / p.limite) * 100;
          const cheio = p.inscricoes.length >= p.limite;
          return (
            <Card key={p.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{p.nome}</span>
                  <span className={`text-sm font-medium ${cheio ? 'text-red-600' : 'text-primary-600'}`}>
                    {p.inscricoes.length} / {p.limite}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
                  <div
                    className={`h-full transition-all ${cheio ? 'bg-red-500' : 'bg-primary-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {p.limite - p.inscricoes.length} {p.limite - p.inscricoes.length === 1 ? 'vaga restante' : 'vagas restantes'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs por passeio */}
      <div className="flex gap-2 border-b border-gray-200">
        {data.map((p) => {
          const ativo = (tabAtivo ?? data[0]?.id) === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setTabAtivo(p.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                ativo
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {p.nome} ({p.inscricoes.length})
            </button>
          );
        })}
      </div>

      {/* Tabela de inscritos do passeio ativo */}
      {passeioAtivo && (
        <Card>
          <CardContent className="p-0">
            {passeioAtivo.inscricoes.length === 0 ? (
              <p className="py-12 text-center text-gray-500">
                Nenhuma inscrição registrada ainda neste passeio.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome completo</TableHead>
                    <TableHead>Clube</TableHead>
                    <TableHead>Data da inscrição</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {passeioAtivo.inscricoes.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="font-medium">{i.nomeCompleto}</TableCell>
                      <TableCell>{i.clube}</TableCell>
                      <TableCell>
                        {new Date(i.createdAt).toLocaleString('pt-BR', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          type="button"
                          onClick={() => handleCancelar(i.id, i.nomeCompleto)}
                          disabled={cancelar.isPending}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Cancelar inscrição"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
